import React, { useCallback, useEffect, useState } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import { FunctionalSelect, SelectItem } from './ui/Select';
import { toast } from 'react-hot-toast';
import { api } from '../services/authService';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  return String(detail);
}

const FIELD_TYPES = [
  { id: 'integer', label: 'Integer' },
  { id: 'real', label: 'Real' },
  { id: 'string', label: 'String' },
  { id: 'boolean', label: 'Boolean' },
  { id: 'date', label: 'Date' },
];

export default function OrganizationPropertiesEditor({
  organizationId,
  apiBase,
  disabled = false,
  disabledReason = '',
}) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState('integer');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!organizationId || disabled) {
      setProperties([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`${apiBase}/${organizationId}/properties`);
      setProperties(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load properties');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, disabled, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const addProperty = async () => {
    if (!organizationId || disabled || !name.trim()) return;
    setSaving(true);
    try {
      await api.post(`${apiBase}/${organizationId}/properties`, {
        name: name.trim().toLowerCase(),
        field_type: fieldType,
        display_name: displayName.trim() || null,
      });
      setName('');
      setDisplayName('');
      toast.success('Property added');
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to add property');
    } finally {
      setSaving(false);
    }
  };

  const removeProperty = async (propertyId) => {
    if (!organizationId || disabled) return;
    try {
      await api.delete(`${apiBase}/${organizationId}/properties/${propertyId}`);
      toast.success('Property removed');
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to remove property');
    }
  };

  if (disabled) {
    return <p className="text-sm text-amber-700">{disabledReason || 'Custom properties are not available.'}</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        These fields are private to this organization. Upload them as extra CSV columns (header
        names must match) or via <code className="text-xs">POST /api/v2/upload_universe_records</code>.
        Removing a definition hides it from segments; uploaded values stay until the next upload
        overwrites them.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[8rem]">
          <label className="mb-1 block text-xs text-gray-500">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
            placeholder="s1"
            disabled={saving}
          />
        </div>
        <div className="min-w-[8rem]">
          <label className="mb-1 block text-xs text-gray-500">Type</label>
          <FunctionalSelect value={fieldType} onValueChange={setFieldType} disabled={saving}>
            {FIELD_TYPES.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </div>
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-xs text-gray-500">Display name (optional)</label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Internal score"
            disabled={saving}
          />
        </div>
        <Button variant="outline" onClick={addProperty} disabled={saving || !name.trim()}>
          {saving ? 'Adding…' : 'Add'}
        </Button>
      </div>
      {fieldType === 'date' ? (
        <p className="text-xs text-gray-500">
          Date values must be ISO format <code className="text-xs">YYYY-MM-DD</code> (for example{' '}
          <code className="text-xs">2026-09-18</code>).
        </p>
      ) : null}
      {loading ? (
        <p className="text-sm text-gray-500">Loading properties…</p>
      ) : properties.length === 0 ? (
        <p className="text-sm text-gray-500">No custom properties yet.</p>
      ) : (
        <div className="space-y-2">
          {properties.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded border px-3 py-2">
              <div>
                <span className="font-mono text-sm">{p.name}</span>
                <span className="ml-2 text-xs text-gray-500">{p.field_type}</span>
                {p.field_type === 'date' ? (
                  <span className="ml-2 text-xs text-gray-500">ISO YYYY-MM-DD</span>
                ) : null}
                {p.display_name ? (
                  <span className="ml-2 text-sm text-gray-700">{p.display_name}</span>
                ) : null}
              </div>
              <Button size="sm" variant="outline" onClick={() => removeProperty(p.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
